import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { decodeToken } from "react-jwt";

import AuthRestAPI from "../../../restAPI/AuthRestAPI";
import moguLogo from "../../../assets/logo/mogu-logo-cover.png";

import "./MoguPortalAuth.css";

const passwordRules = [
  { id: 1, text: "At least 8 characters", pattern: /.{8,}/ },
  { id: 2, text: "At most 15 characters", pattern: /^.{0,15}$/ },
  { id: 3, text: "One uppercase letter", pattern: /[A-Z]/ },
  { id: 4, text: "One lowercase letter", pattern: /[a-z]/ },
  { id: 5, text: "One number", pattern: /\d/ },
  { id: 6, text: "One special character (@$!%*?&)", pattern: /[@$!%*?&]/ },
];

const REDIRECT_DELAY_MS = 3000;

const FirstTimeResetPassword = () => {
  const navigate = useNavigate();

  const [view, setView] = useState("form");
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (window.location.href.includes("firsttimeresetpassword/:tokenId?")) {
      const parts = window.location.href.split("/:tokenId?");
      const rawToken = parts[1] || "";
      try {
        const decoded = decodeToken(rawToken);
        setToken(rawToken);
        setEmail(decoded?.email || "");
      } catch {
        setToken(rawToken);
      }
    }
  }, []);

  const ruleResults = useMemo(
    () => passwordRules.map((r) => ({ ...r, ok: r.pattern.test(password) })),
    [password]
  );

  const allRulesValid = useMemo(
    () => ruleResults.every((r) => r.ok),
    [ruleResults]
  );

  const passwordsMatch = useMemo(
    () => confirmPassword.length > 0 && confirmPassword === password,
    [confirmPassword, password]
  );

  const handleSubmit = (e) => {
    e?.preventDefault();
    setSubmitError("");

    const newErrors = {};
    if (!allRulesValid) {
      newErrors.password = "Password does not meet all the requirements.";
    }
    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your new password.";
    } else if (confirmPassword !== password) {
      newErrors.confirmPassword = "The two passwords do not match.";
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);
    const formData = {
      email,
      newPassword: password,
      confirmNewPassword: password,
    };

    AuthRestAPI.firstTimeResetPassword(
      token,
      localStorage.getItem("groupId"),
      localStorage.getItem("companyId"),
      formData
    )
      .then(() => {
        setLoading(false);
        setView("success");
        setTimeout(() => navigate("/login"), REDIRECT_DELAY_MS);
      })
      .catch((err) => {
        setLoading(false);
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Something went wrong. Please try again.";
        setSubmitError(msg);
      });
  };

  return (
    <div className="mogu-auth-page">
      <div className="bg-mesh" aria-hidden="true">
        <div className="auth-bg-glow auth-bg-glow--top" />
        <div className="auth-bg-glow auth-bg-glow--bottom" />
      </div>

      <div className="card-wrap">
        <div className="auth-cover-logo">
          <img src={moguLogo} alt="MOGU logo" />
        </div>
        <div className="card">
          {view === "form" && (
            <div className="view active animate-in" id="firstTimeResetView">
              <div className="reset-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 1l3 5 5 1-4 4 1 6-5-3-5 3 1-6-4-4 5-1z" />
                </svg>
              </div>

              <div className="headline-block">
                <div className="welcome-kicker">Welcome to Mogu</div>
                <h1 className="headline">
                  Create your <span className="highlight">new password</span>.
                </h1>
                <p className="sub">
                  Set a strong password for your account before you start using
                  the portal.
                </p>
              </div>

              {submitError && (
                <div className="inline-alert error" role="alert">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span>{submitError}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                {email && (
                  <div className="email-display" aria-label="Account email">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="4" width="20" height="16" rx="2" />
                      <path d="M22 7l-10 6L2 7" />
                    </svg>
                    <span className="label">Account</span>
                    <span>{email}</span>
                  </div>
                )}

                <div className="field-group" style={{ marginTop: 16 }}>
                  <label htmlFor="ft-new-pw">New password</label>
                  <div className="input-box">
                    <input
                      id="ft-new-pw"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your new password"
                      autoComplete="new-password"
                      value={password}
                      className={errors.password ? "has-error" : ""}
                      style={{ paddingRight: "48px" }}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (errors.password) {
                          setErrors((prev) => ({ ...prev, password: "" }));
                        }
                      }}
                    />
                    <svg className="prefix" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" />
                      <path d="M7 11V7a5 5 0 0110 0v4" />
                    </svg>
                    <button
                      type="button"
                      className="pw-toggle"
                      onClick={() => setShowPassword((s) => !s)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.password && <span className="field-error">{errors.password}</span>}

                  {password && (
                    <div className="pw-rules" aria-live="polite">
                      {ruleResults.map((r) => (
                        <div key={r.id} className={`pw-rule ${r.ok ? "ok" : ""}`}>
                          {r.ok ? (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10" />
                            </svg>
                          )}
                          <span>{r.text}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="field-group">
                  <label htmlFor="ft-confirm-pw">Confirm password</label>
                  <div className="input-box">
                    <input
                      id="ft-confirm-pw"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Re-enter your new password"
                      autoComplete="new-password"
                      value={confirmPassword}
                      className={errors.confirmPassword ? "has-error" : ""}
                      style={{ paddingRight: "48px" }}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        if (errors.confirmPassword) {
                          setErrors((prev) => ({ ...prev, confirmPassword: "" }));
                        }
                      }}
                    />
                    <svg className="prefix" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" />
                      <path d="M7 11V7a5 5 0 0110 0v4" />
                    </svg>
                    <button
                      type="button"
                      className="pw-toggle"
                      onClick={() => setShowConfirmPassword((s) => !s)}
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      {showConfirmPassword ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <span className="field-error">{errors.confirmPassword}</span>
                  )}
                  {!errors.confirmPassword && confirmPassword && (
                    <span
                      className="field-error"
                      style={{ color: passwordsMatch ? "var(--green)" : "var(--red)" }}
                    >
                      {passwordsMatch ? "Passwords match" : "Passwords do not match"}
                    </span>
                  )}
                </div>

                <button
                  className="submit"
                  type="submit"
                  disabled={loading}
                  style={{ marginTop: 24 }}
                >
                  <span>
                    {loading ? (
                      <>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="spin">
                          <path d="M21 12a9 9 0 11-6.219-8.56" />
                        </svg>
                        Updating...
                      </>
                    ) : (
                      <>
                        Set password & continue
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </>
                    )}
                  </span>
                </button>
              </form>
            </div>
          )}

          {view === "success" && (
            <div className="view active animate-in" id="firstTimeSuccessView">
              <div className="success-state">
                <div className="success-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                    <path d="M22 4L12 14.01l-3-3" />
                  </svg>
                </div>

                <div className="welcome-kicker" style={{ justifyContent: "center" }}>
                  All set
                </div>
                <h1 className="headline">
                  Password <span className="highlight">updated</span>.
                </h1>
                <p className="sub">
                  You can now sign in with your new password.
                </p>
                <p className="sub" style={{ fontSize: "14px", marginTop: 8 }}>
                  Redirecting you to the sign-in page...
                </p>

                <button
                  type="button"
                  className="submit"
                  onClick={() => navigate("/login")}
                  style={{ marginTop: 24 }}
                >
                  <span>
                    Go to sign in
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FirstTimeResetPassword;
